import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// ─── Shield SVG (shared with ProgressTracker) ───────────────────────────────

export function ShieldIcon({
  size = 24,
  filled = false,
  glowIntensity = 0,
}: {
  size?: number;
  filled?: boolean;
  glowIntensity?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      style={
        glowIntensity > 0
          ? { filter: `drop-shadow(0 0 ${4 + glowIntensity * 6}px rgba(168, 180, 192, ${glowIntensity * 0.5}))` }
          : undefined
      }
    >
      <path
        d="M12 2L3 7v5c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-9-5z"
        fill={filled ? 'rgba(168, 180, 192, 0.15)' : 'none'}
        stroke={filled ? '#A8B4C0' : 'rgba(255,255,255,0.2)'}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Ember particles ────────────────────────────────────────────────────────

function EmberBurst({ count, intensity }: { count: number; intensity: number }) {
  const embers = Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 + (Math.random() - 0.5) * 0.6;
    const distance = 50 + Math.random() * 70 * intensity;
    const size = 2 + Math.random() * 3;
    return {
      id: i,
      tx: Math.cos(angle) * distance,
      ty: Math.sin(angle) * distance,
      size,
      dur: 0.5 + Math.random() * 0.4,
      delay: Math.random() * 0.12,
      bright: Math.random() > 0.4,
    };
  });

  return (
    <>
      {embers.map((e) => (
        <motion.div
          key={e.id}
          className="absolute rounded-full"
          style={{
            width: e.size,
            height: e.size,
            backgroundColor: e.bright
              ? `rgba(255, 200, 100, ${0.6 + Math.random() * 0.4})`
              : `rgba(232, 148, 58, ${0.5 + Math.random() * 0.5})`,
            left: '50%',
            top: '50%',
            marginLeft: -e.size / 2,
            marginTop: -e.size / 2,
          }}
          initial={{ x: 0, y: 0, scale: 1, opacity: 1 }}
          animate={{ x: e.tx, y: e.ty, scale: 0, opacity: 0 }}
          transition={{ duration: e.dur, delay: e.delay, ease: 'easeOut' }}
        />
      ))}
    </>
  );
}

// ─── Standard forge overlay ─────────────────────────────────────────────────

function ForgeOverlay({
  stepLabel,
  intensity,
  onComplete,
}: {
  stepLabel: string;
  intensity: number; // 0-1, higher = more embers
  onComplete: () => void;
}) {
  const emberCount = Math.round(6 + intensity * 14);

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      {/* Backdrop */}
      <motion.div
        className="absolute inset-0 bg-[#060A12]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.94 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      />

      {/* Radial pulse */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: [0, 0.6, 0] }}
        transition={{ duration: 0.7, times: [0, 0.35, 1] }}
      >
        <div
          className="w-full h-full"
          style={{
            background: 'radial-gradient(circle at center, rgba(232, 148, 58, 0.12) 0%, transparent 45%)',
          }}
        />
      </motion.div>

      {/* Central forge moment */}
      <div className="relative z-10">
        <motion.div
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
        >
          <EmberBurst count={emberCount} intensity={1 + intensity} />
        </motion.div>

        <motion.div
          className="relative"
          initial={{ scale: 0.4, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring' as const, stiffness: 350, damping: 22, delay: 0.05 }}
          onAnimationComplete={() => setTimeout(onComplete, 350)}
        >
          {/* Glow ring */}
          <motion.div
            className="absolute -inset-10 rounded-full"
            animate={{
              boxShadow: [
                '0 0 15px rgba(232, 148, 58, 0.08)',
                '0 0 50px rgba(232, 148, 58, 0.25)',
                '0 0 25px rgba(232, 148, 58, 0.12)',
              ],
            }}
            transition={{ duration: 0.5, times: [0, 0.5, 1] }}
          />

          {/* Shield heating up */}
          <motion.div
            animate={{
              filter: [
                'brightness(0.4) saturate(0.2)',
                'brightness(1.6) saturate(1.3)',
                'brightness(1.0) saturate(1.0)',
              ],
            }}
            transition={{ duration: 0.5, times: [0, 0.55, 1] }}
          >
            <ShieldIcon size={72} filled glowIntensity={0.8} />
          </motion.div>

          {/* Step name */}
          <motion.p
            className="absolute -bottom-9 left-1/2 -translate-x-1/2 text-[10px] font-mono text-[#A8B4C0]/60 tracking-[0.25em] uppercase whitespace-nowrap"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            {stepLabel}
          </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
}

// ─── Prescription forge (shields converge → shatter) ────────────────────────

function PrescriptionForge({
  completedSteps,
  onComplete,
}: {
  completedSteps: number;
  onComplete: () => void;
}) {
  return (
    <motion.div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.12 }}
    >
      <motion.div
        className="absolute inset-0 bg-[#060A12]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.96 }}
        exit={{ opacity: 0 }}
      />

      <div className="relative z-10">
        {/* Converging shields */}
        {Array.from({ length: completedSteps }, (_, i) => {
          const startX = (i - (completedSteps - 1) / 2) * 100;
          return (
            <motion.div
              key={i}
              className="absolute"
              style={{ left: '50%', top: '50%', marginLeft: -16, marginTop: -16 }}
              initial={{ x: startX, y: -15, scale: 1, opacity: 0.7 }}
              animate={{ x: 0, y: 0, scale: 0.7, opacity: 1 }}
              transition={{ duration: 0.35, delay: i * 0.06, ease: 'easeInOut' }}
            >
              <ShieldIcon size={32} filled glowIntensity={0.5} />
            </motion.div>
          );
        })}

        {/* Ember burst */}
        <motion.div
          className="absolute"
          style={{ left: '50%', top: '50%' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.45 }}
        >
          <EmberBurst count={24} intensity={2.5} />
        </motion.div>

        {/* Shatter fragments */}
        {[
          { x: -180, y: -160, r: -35 },
          { x: 180, y: -160, r: 35 },
          { x: -180, y: 160, r: -125 },
          { x: 180, y: 160, r: 125 },
        ].map((t, i) => (
          <motion.div
            key={`shard-${i}`}
            className="absolute"
            style={{
              left: '50%',
              top: '50%',
              marginLeft: -10,
              marginTop: -10,
              width: 20,
              height: 20,
              clipPath: [
                'polygon(50% 0%, 0% 100%, 50% 65%)',
                'polygon(50% 0%, 100% 100%, 50% 65%)',
                'polygon(0% 0%, 50% 35%, 0% 100%)',
                'polygon(100% 0%, 50% 35%, 100% 100%)',
              ][i],
              backgroundColor: '#A8B4C0',
            }}
            initial={{ x: 0, y: 0, scale: 1, opacity: 0.8, rotate: 0 }}
            animate={{ x: t.x, y: t.y, scale: 0.2, opacity: 0, rotate: t.r }}
            transition={{ delay: 0.5, duration: 0.45, ease: 'easeOut' }}
          />
        ))}

        {/* Flash */}
        <motion.div
          className="fixed inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 0.35, 0] }}
          transition={{ delay: 0.48, duration: 0.35, times: [0, 0.25, 1] }}
          onAnimationComplete={() => setTimeout(onComplete, 250)}
        >
          <div className="w-full h-full bg-[#A8B4C0]/15" />
        </motion.div>
      </div>
    </motion.div>
  );
}

// ─── Route-to-step mapping ──────────────────────────────────────────────────

/** Maps route paths to their step number (1-based, matching ProgressTracker) */
const ROUTE_STEP_MAP: Record<string, { step: number; label: string }> = {
  '/auth': { step: 1, label: 'Sign In' },
  '/assess': { step: 2, label: 'Assess' },
  '/learn': { step: 3, label: 'Learn' },
  '/resources': { step: 3, label: 'Learn' },
  '/stats/backtest': { step: 4, label: 'Stats' },
  '/stats/proven': { step: 4, label: 'Stats' },
  '/firm': { step: 5, label: 'Firm' },
  '/prescription': { step: 6, label: 'Protocol' },
  '/dashboard': { step: 7, label: 'Dashboard' },
};

const PRESCRIPTION_STEP = 6;
const TOTAL_JOURNEY_STEPS = 7;

// ─── Forge Context ──────────────────────────────────────────────────────────

interface ForgeContextValue {
  /** Navigate with forge transition. Only triggers forge on forward journey progression. */
  forgeNavigate: (to: string, currentStep?: number) => void;
}

const ForgeContext = createContext<ForgeContextValue>({
  forgeNavigate: () => {},
});

export function useForgeNavigate() {
  return useContext(ForgeContext).forgeNavigate;
}

// ─── Provider ───────────────────────────────────────────────────────────────

export function ForgeTransitionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [forgeState, setForgeState] = useState<{
    type: 'standard' | 'prescription';
    label: string;
    intensity: number;
    completedSteps: number;
    destination: string;
  } | null>(null);

  const forgeNavigate = useCallback(
    (to: string, currentStep?: number) => {
      const target = ROUTE_STEP_MAP[to];

      // If no mapping or going backward/sideways, just navigate normally
      if (!target || !currentStep || target.step <= currentStep) {
        navigate(to);
        return;
      }

      const intensity = Math.min(target.step / TOTAL_JOURNEY_STEPS, 1);

      if (target.step === PRESCRIPTION_STEP) {
        // Special prescription forge
        setForgeState({
          type: 'prescription',
          label: target.label,
          intensity,
          completedSteps: currentStep, // how many shields completed before protocol
          destination: to,
        });
      } else {
        // Standard forge
        setForgeState({
          type: 'standard',
          label: target.label,
          intensity,
          completedSteps: currentStep,
          destination: to,
        });
      }
    },
    [navigate],
  );

  const handleForgeComplete = useCallback(() => {
    if (!forgeState) return;
    const dest = forgeState.destination;
    setForgeState(null);
    navigate(dest);
  }, [forgeState, navigate]);

  return (
    <ForgeContext.Provider value={{ forgeNavigate }}>
      {children}

      <AnimatePresence>
        {forgeState?.type === 'standard' && (
          <ForgeOverlay
            key="forge-standard"
            stepLabel={forgeState.label}
            intensity={forgeState.intensity}
            onComplete={handleForgeComplete}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {forgeState?.type === 'prescription' && (
          <PrescriptionForge
            key="forge-prescription"
            completedSteps={Math.min(forgeState.completedSteps, 5)}
            onComplete={handleForgeComplete}
          />
        )}
      </AnimatePresence>
    </ForgeContext.Provider>
  );
}
